import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExtensionIcon from "@mui/icons-material/Extension";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CloseIcon from "@mui/icons-material/Close";
import { api } from "api/config";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import { DIVIDER } from "components/bento/bentoTokens";

const buildSteps = (fileName) => [
  `ดาวน์โหลดไฟล์ ${fileName} แล้วแตกไฟล์ (Extract) ไปยังโฟลเดอร์ที่ต้องการ`,
  'เปิด Chrome แล้วไปที่ chrome://extensions',
  'เปิด "Developer mode" (มุมขวาบน)',
  'คลิก "Load unpacked" แล้วเลือกโฟลเดอร์ที่แตกไฟล์ไว้',
  'Extension ชื่อ "Winner Express" จะปรากฏในแถบ Extensions',
  'เปิดหน้า Order detail ใน Lazada หรือ Shopee แล้วคลิก Icon ของ Extension',
];

const ChromeExtension = () => {
  const [downloading, setDownloading] = useState(false);
  const [extVersion, setExtVersion] = useState(null);
  const [appVersion, setAppVersion] = useState(null);

  // Installation manual (PDF) modal state
  const [manualOpen, setManualOpen] = useState(false);
  const [manualUrl, setManualUrl] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState(false);

  const zipFileName = `winner-extension${extVersion?.version ? `-${extVersion.version}` : ""}.zip`;
  const steps = buildSteps(zipFileName);

  useEffect(() => {
    api().get("/public/extension-version")
      .then((r) => setExtVersion(r.data))
      .catch(() => {});
    api().get("/public/app-version")
      .then((r) => setAppVersion(r.data?.version))
      .catch(() => {});
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch("/api/extension/download", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Extension download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  // Fetch the manual PDF as a blob (sends auth cookie via credentials) once,
  // then reuse the object URL for both the in-modal viewer and the download.
  const ensureManualUrl = async () => {
    if (manualUrl) return manualUrl;
    setManualLoading(true);
    setManualError(false);
    try {
      const res = await fetch("/api/extension/manual", { credentials: "include" });
      if (!res.ok) throw new Error("Manual fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setManualUrl(url);
      return url;
    } catch (err) {
      console.error("Extension manual error:", err);
      setManualError(true);
      return null;
    } finally {
      setManualLoading(false);
    }
  };

  const handleOpenManual = () => {
    setManualOpen(true);
    ensureManualUrl();
  };

  const handleDownloadManual = async () => {
    const url = await ensureManualUrl();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = "winner-extension-install-guide.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Release the blob URL when leaving the page
  useEffect(() => {
    return () => {
      if (manualUrl) URL.revokeObjectURL(manualUrl);
    };
  }, [manualUrl]);

  return (
    <PageShell
      title="Chrome Extension"
      subtitle={`Winner Express — Lazada & Shopee Order Import${appVersion ? `  |  App v${appVersion}` : ''}`}
    >
      <Box display="flex" gap={3} flexWrap="wrap">
        {/* Download card */}
        <BentoCard sx={{ minWidth: 300, flex: "0 0 auto" }} bodySx={{ p: 0 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ExtensionIcon color="secondary" />
              <Typography variant="h6" fontWeight="bold">
                Winner Express Extension
              </Typography>
            </Box>

            <Box display="flex" gap={1} mb={3} flexWrap="wrap">
              <Chip label="Lazada" size="small" color="error" />
              <Chip label="Shopee" size="small" color="warning" />
              <Chip label="Manifest V3" size="small" variant="outlined" />
              {extVersion?.version && (
                <Chip
                  icon={<NewReleasesIcon />}
                  label={`v${extVersion.version}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>

            {extVersion?.changelog?.length > 0 && (
              <Box mb={2} p={1.5} borderRadius={1} bgcolor="action.hover">
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  What's new in v{extVersion.version}
                </Typography>
                {extVersion.changelog.map((item, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" display="block">
                    · {item}
                  </Typography>
                ))}
                {extVersion.releaseDate && (
                  <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                    {extVersion.releaseDate}
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" mb={3}>
              ดาวน์โหลด Extension สำหรับนำเข้าข้อมูล Order จาก Lazada และ Shopee เข้าระบบ Winner Express ได้ในคลิกเดียว
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading}
              fullWidth
            >
              {downloading ? "กำลังดาวน์โหลด..." : `ดาวน์โหลด ${zipFileName}`}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<MenuBookIcon />}
              onClick={handleOpenManual}
              fullWidth
              sx={{ mt: 1.5 }}
            >
              ดูคู่มือการติดตั้ง (PDF)
            </Button>
          </CardContent>
        </BentoCard>

        {/* Installation steps */}
        <BentoCard sx={{ flex: "1 1 320px" }} bodySx={{ p: 0 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              วิธีติดตั้ง
            </Typography>
            <Divider sx={{ mb: 2, borderColor: DIVIDER }} />
            <List dense disablePadding>
              {steps.map((step, i) => (
                <ListItem key={i} alignItems="flex-start" disableGutters sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    <CheckCircleOutlineIcon fontSize="small" color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2">{step}</Typography>}
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2, borderColor: DIVIDER }} />
            <Typography variant="caption" color="text.secondary">
              หมายเหตุ: ต้องล็อกอินที่ winner.express ก่อน Extension จึงจะเชื่อมต่อ API ได้
            </Typography>
          </CardContent>
        </BentoCard>
      </Box>

      {/* Installation manual (PDF) viewer */}
      <Dialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
        >
          <MenuBookIcon color="secondary" />
          <Typography variant="h6" fontWeight="bold" component="span">
            คู่มือการติดตั้ง Chrome Extension
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setManualOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: "action.hover" }}>
          {manualLoading && (
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <CircularProgress color="secondary" />
              <Typography variant="body2" color="text.secondary">
                กำลังโหลดคู่มือ...
              </Typography>
            </Box>
          )}

          {manualError && !manualLoading && (
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
              p={3}
            >
              <Typography variant="body2" color="error" textAlign="center">
                ไม่สามารถโหลดคู่มือได้ กรุณาลองดาวน์โหลดไฟล์แทน
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadManual}
              >
                ดาวน์โหลด PDF
              </Button>
            </Box>
          )}

          {manualUrl && !manualLoading && !manualError && (
            <iframe
              title="Chrome Extension Install Manual"
              src={manualUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadManual}
          >
            ดาวน์โหลด PDF
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setManualOpen(false)}
          >
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ChromeExtension;
